/** 推送只读分析的筛选与投影；指标唯一口径为 dev-docs A4 §10。 */
import { z } from 'zod';
import { PUSH_TEMPLATE_CATEGORIES } from './push';

export const PUSH_ANALYTICS_LOOKBACK_DAYS = 31;
export const PUSH_ANALYTICS_MESSAGE_LIMIT = 50_000;
export const PUSH_ANALYTICS_DELIVERY_LIMIT = 200_000;
export const PUSH_ANALYTICS_GROUP_LIMIT = 2_000;
export const PUSH_ANALYTICS_CACHE_SECONDS = 60;
export const PushMessageStatusSchema = z.enum(['PENDING', 'DISPATCHED', 'OPENED']);
export const PushDeliveryStatusSchema = z.enum([
  'PENDING',
  'SENDING',
  'SENT',
  'FAILED_RETRIABLE',
  'FAILED_PERMANENT',
]);
export const PushAnalyticsTimezoneSchema = z
  .string()
  .min(1)
  .max(100)
  .refine((value) => {
    try {
      if (value !== 'UTC' && !value.includes('/')) return false;
      new Intl.DateTimeFormat('en', { timeZone: value }).format();
      return true;
    } catch {
      return false;
    }
  }, '统计时区须为有效 IANA 时区');
const BooleanQuerySchema = z
  .union([z.boolean(), z.enum(['true', 'false'])])
  .transform((v) => v === true || v === 'true');
export const PushAnalyticsQuerySchema = z
  .object({
    start_at: z.iso.datetime({ offset: true }),
    end_at: z.iso.datetime({ offset: true }),
    report_timezone: PushAnalyticsTimezoneSchema.default('UTC'),
    app_id: z.uuid().optional(),
    template_slug: z.string().min(1).max(64).optional(),
    category: z.enum(PUSH_TEMPLATE_CATEGORIES).optional(),
    notification_kind: z.enum(['MARKETING', 'FUNCTIONAL']).optional(),
    rendered_language: z
      .string()
      .min(2)
      .max(16)
      .regex(/^[a-zA-Z]{2,3}(?:[-_][a-zA-Z0-9]{2,8})*$/)
      .optional(),
    message_status: PushMessageStatusSchema.optional(),
    notification_id: z.uuid().optional(),
    user_uid: z
      .string()
      .trim()
      .toUpperCase()
      .regex(/^[0-9A-HJKMNP-TV-Z]{10}$/, '业务 UID 须为10位 Crockford 编号')
      .optional(),
    include_test_data: BooleanQuerySchema.default(false),
    platform: z.enum(['IOS', 'ANDROID']).optional(),
    vendor: z.enum(['APNS', 'FCM']).optional(),
    delivery_status: PushDeliveryStatusSchema.optional(),
  })
  .strict();
export const PushAnalyticsListQuerySchema = PushAnalyticsQuerySchema.extend({
  page: z.coerce.number().int().min(1).max(1_000_000).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});
export const PushAnalyticsDetailQuerySchema = z.object({ app_id: z.uuid().optional() }).strict();
export type PushAnalyticsQuery = z.infer<typeof PushAnalyticsQuerySchema>;
export type PushAnalyticsFilter = PushAnalyticsQuery;
export type PushAnalyticsListQuery = z.infer<typeof PushAnalyticsListQuerySchema>;
/** 相对窗口在每次请求时校验，缓存不改变可查询范围。 */
export function validatePushAnalyticsWindow(query: PushAnalyticsQuery, now: Date): boolean {
  const start = Date.parse(query.start_at);
  const end = Date.parse(query.end_at);
  return (
    start < end &&
    end <= now.getTime() &&
    start >= now.getTime() - PUSH_ANALYTICS_LOOKBACK_DAYS * 86_400_000
  );
}
const Count = z.number().int().nonnegative();
const Share = z.number().min(0).max(1).nullable();
export const PushAnalyticsDeviceCountsSchema = z.object({
  device_total: Count,
  provider_accepted: Count,
  provider_acceptance_share: Share,
  device_pending: Count,
  device_sending: Count,
  device_failed_retriable: Count,
  device_failed_permanent: Count,
});
export const PushAnalyticsMetricCountsSchema = PushAnalyticsDeviceCountsSchema.extend({
  message_created: Count,
  message_dispatched: Count,
  message_opened: Count,
  dispatched_opened: Count,
  opened_without_dispatch: Count,
  open_rate: Share,
});
export type MetricCounts = z.infer<typeof PushAnalyticsMetricCountsSchema>;
export type PushAnalyticsMetricCounts = MetricCounts;
export type PushAnalyticsDeviceCounts = z.infer<typeof PushAnalyticsDeviceCountsSchema>;
export const PushAnalyticsResponseSchema = z.object({
  meta: z.object({
    range_start_at: z.string(),
    range_end_at: z.string(),
    report_timezone: z.string(),
    statistics_at: z.string(),
    coverage_start_at: z.string(),
    lookback_days: z.literal(31),
    retention_days: z.literal(90),
    cache_ttl_seconds: Count,
    cache_hit: z.boolean(),
    anonymized_messages: Count,
    test_classification_unknown_messages: Count,
    delivery_history_cleared_messages: Count,
    coverage_notes: z.array(z.string()),
  }),
  summary: PushAnalyticsMetricCountsSchema,
  trend: z.array(PushAnalyticsMetricCountsSchema.extend({ date: z.string() })),
  templates: z.array(
    PushAnalyticsMetricCountsSchema.extend({
      template_slug: z.string(),
      rendered_language: z.string(),
    }),
  ),
  unavailable_metrics: z.array(
    z.object({ key: z.string(), label: z.string(), reason: z.string() }),
  ),
});
export type PushAnalyticsResponse = z.infer<typeof PushAnalyticsResponseSchema>;
export const PushAnalyticsTestDataKindSchema = z.enum([
  'TEMPLATE_TEST',
  'DEV_QA_1151',
  'INTERNAL_ACCOUNT',
  'UNKNOWN_AFTER_ANONYMIZATION',
  'BUSINESS',
]);
export const PushAnalyticsNotificationSchema = z.object({
  notification_id: z.uuid(),
  app_id: z.uuid(),
  app_name: z.string(),
  user_uid: z.string().nullable(),
  template_slug: z.string(),
  category: z.enum(PUSH_TEMPLATE_CATEGORIES),
  rendered_language: z.string(),
  status: PushMessageStatusSchema,
  created_at: z.string(),
  sent_at: z.string().nullable(),
  read_at: z.string().nullable(),
  opened_at: z.string().nullable(),
  has_link: z.boolean(),
  deliver_before: z.string().nullable(),
  test_data_kind: PushAnalyticsTestDataKindSchema,
  device_data_state: z.enum(['AVAILABLE', 'NO_RECORDS', 'CLEARED_PRIVACY']),
  device_summary: PushAnalyticsDeviceCountsSchema.nullable(),
});
export type PushAnalyticsNotification = z.infer<typeof PushAnalyticsNotificationSchema>;
export const PushAnalyticsNotificationDetailSchema = z.object({
  statistics_at: z.string(),
  notification: PushAnalyticsNotificationSchema,
  deliveries: z.array(
    z.object({
      id: z.uuid(),
      platform: z.enum(['IOS', 'ANDROID']),
      vendor: z.enum(['APNS', 'FCM']),
      delivery_status: PushDeliveryStatusSchema,
      created_at: z.string(),
      claimed_at: z.string().nullable(),
      sent_at: z.string().nullable(),
      failed_at: z.string().nullable(),
      failure_reason: z.object({ code: z.string(), label: z.string() }),
    }),
  ),
  delivery_records_truncated: z.boolean(),
});
export type PushAnalyticsNotificationDetail = z.infer<typeof PushAnalyticsNotificationDetailSchema>;
